import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Flag, Users, Mountain, TreePine, Waves, Home as HomeIcon, Landmark, Leaf, Sparkles } from "lucide-react";
import api from "@/lib/api";
import EventCard from "@/components/EventCard";
import RegisterModal from "@/components/RegisterModal";
import PartnersMarquee from "@/components/PartnersMarquee";

const HERO_CLIPS = [
  "https://assets.mixkit.co/videos/33805/33805-720.mp4",
  "https://assets.mixkit.co/videos/45414/45414-720.mp4",
  "https://assets.mixkit.co/videos/12974/12974-720.mp4",
  "https://assets.mixkit.co/videos/6799/6799-720.mp4",
];
const HERO_POSTER = "https://images.unsplash.com/photo-1452626038306-9aae5e071dd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxtYXJhdGhvbiUyMHJ1bm5lcnMlMjByYWNlJTIwc3RhcnR8ZW58MHx8fHwxNzg5MDY4MTkwfDA&ixlib=rb-4.1.0&q=85";

function HeroMontage() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_CLIPS.length), 8000);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="hero-video" className="absolute inset-0">
      <img src={HERO_POSTER} alt="" className="absolute inset-0 h-full w-full object-cover" />
      {HERO_CLIPS.map((src, i) => (
        <video
          key={src}
          src={src}
          muted
          playsInline
          preload={i < 2 ? "auto" : "metadata"}
          poster={i === 0 ? HERO_POSTER : undefined}
          onEnded={() => setIdx((i + 1) % HERO_CLIPS.length)}
          ref={(el) => {
            if (!el) return;
            if (i === idx) { el.play().catch(() => {}); } else { el.pause(); }
          }}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${i === idx ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      <div className="absolute bottom-6 right-6 z-10 flex gap-1.5">
        {HERO_CLIPS.map((_, i) => (
          <span key={i} className={`h-1 rounded-full transition-all duration-500 ${i === idx ? "w-6 bg-brand-olive" : "w-2 bg-white/40"}`} />
        ))}
      </div>
    </div>
  );
}

const WHY = [
  { icon: Flag, title: "Professionally Managed Events", text: "Timed races, trained volunteers and race-day operations built to national standards." },
  { icon: Compass, title: "Offbeat Destinations", text: "We take running beyond the city — into mountains, forests, beaches, villages and heritage landscapes." },
  { icon: Users, title: "Community First", text: "Local youth, homestays, schools and businesses are part of every event we create." },
  { icon: Sparkles, title: "Experiences, Not Just Races", text: "Culture, cuisine, safaris and stories — every SPORTIVE event is a destination journey." },
];

const LANDSCAPES = [
  { name: "MOUNTAINS", tag: "HILL", img: "https://images.unsplash.com/photo-1776334597769-0fcc3df1c649?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHx0cmFpbCUyMHJ1bm5pbmclMjBtb3VudGFpbnMlMjBoaW1hbGF5YXxlbnwwfHx8fDE3ODkwNjgxOTB8MA&ixlib=rb-4.1.0&q=85" },
  { name: "FORESTS & WILDLIFE", tag: "FOREST", img: "https://images.unsplash.com/photo-1607441078533-77ba814b1e5d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxzdW5kYXJiYW4lMjBtYW5ncm92ZSUyMGZvcmVzdCUyMGluZGlhfGVufDB8fHx8MTc4OTA2ODE4OXww&ixlib=rb-4.1.0&q=85" },
  { name: "BEACHES", tag: "BEACH", img: "https://images.unsplash.com/photo-1761662826640-3e3385418f89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxiZWFjaCUyMHJ1bm5pbmclMjBzdW5yaXNlJTIwaW5kaWElMjBjb2FzdHxlbnwwfHx8MTc4OTA2ODIwN3ww&ixlib=rb-4.1.0&q=85" },
  { name: "VILLAGES", tag: "VILLAGE", img: "https://images.unsplash.com/photo-1651678938439-fc3f8f03eda5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxydXJhbCUyMGluZGlhJTIwdmlsbGFnZSUyMGdyZWVuJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc4OTA2ODIwN3ww&ixlib=rb-4.1.0&q=85" },
  { name: "HERITAGE", tag: "HERITAGE", img: "https://images.unsplash.com/photo-1603766806347-54cdf3745953?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxpbmRpYSUyMGhlcml0YWdlJTIwYW5jaWVudCUyMHRlbXBsZSUyMG1vbnVtZW50fGVufDB8fHx8MTc4OTA2ODIwN3ww&ixlib=rb-4.1.0&q=85" },
  { name: "TEA LANDSCAPES", tag: "TEA", img: "https://images.unsplash.com/photo-1455157823797-3019317cbcf0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHx0ZWElMjBwbGFudGF0aW9uJTIwZm9yZXN0JTIwbGFuZHNjYXBlJTIwcnVubmluZyUyMHRyYWlsfGVufDB8fHx8MTc4OTA2ODEyM3ww&ixlib=rb-4.1.0&q=85" },
];

const IMPACT = [
  ["01", "DESTINATION VISIBILITY", "Running events introduce emerging destinations to new audiences through participants, running communities and digital media."],
  ["02", "TOURISM & REVENUE", "Participants and accompanying travellers contribute to local accommodation, transport, food and tourism services."],
  ["03", "LOCAL LIVELIHOODS", "Events create opportunities for transport providers, hotels, restaurants, event crews, suppliers and service providers."],
  ["04", "FITNESS CULTURE", "Running events encourage participation, physical activity and awareness around healthy lifestyles."],
  ["05", "CULTURAL EXCHANGE", "Participants experience local traditions, food, heritage and communities while destinations connect with visitors from across India."],
];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [stories, setStories] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [regEvent, setRegEvent] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/stories").then((r) => setStories(r.data.slice(0, 3))).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  const upcoming = events
    .filter((e) => e.status === "registration_open" || e.status === "coming_soon")
    .sort((a, b) => (a.event_date || "9999-12-31").localeCompare(b.event_date || "9999-12-31"));
  const featured = events.filter((e) => e.featured && e.status === "completed");
  const states = new Set(events.map((e) => e.state)).size;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="grain relative flex min-h-[92vh] items-center overflow-hidden">
        <HeroMontage />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/95 via-brand-ink/60 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-brand-ink to-transparent" />
        <div className="relative mx-auto w-full max-w-7xl px-4 py-28 sm:px-6">
          <p className="eyebrow animate-fade-up">SPORTIVE — SPORTS TOURISM • MARATHONS • OFFBEAT DESTINATIONS</p>
          <h1 className="mt-5 font-display text-6xl font-extrabold uppercase leading-[0.9] tracking-tight text-white animate-fade-up sm:text-7xl lg:text-8xl" style={{ animationDelay: "0.12s" }}>
            Run <span className="text-brand-cyan">•</span> Explore <span className="text-brand-olive">•</span> Connect
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 animate-fade-up sm:text-lg" style={{ animationDelay: "0.24s" }}>
            Creating unforgettable running experiences while putting India's lesser-known destinations on the map. SPORTIVE brings together running, adventure, tourism, culture and communities through professionally managed sporting events across India.
          </p>
          <div className="mt-9 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "0.36s" }}>
            <Link to="/events" data-testid="hero-explore-events" className="btn-kinetic btn-primary-k">EXPLORE EVENTS <ArrowRight size={16} /></Link>
            <Link to="/runsafari" data-testid="hero-discover-runsafari" className="btn-kinetic btn-ghost-k">DISCOVER RUNSAFARI</Link>
          </div>
          <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4 font-mono text-xs tracking-[0.2em] text-slate-400 animate-fade-up" style={{ animationDelay: "0.5s" }}>
            <span><strong className="mr-2 text-2xl font-semibold text-brand-cyan">{events.length || "20"}+</strong>EVENTS</span>
            <span><strong className="mr-2 text-2xl font-semibold text-brand-olive">{states || "10"}+</strong>STATES</span>
            <span><strong className="mr-2 text-2xl font-semibold text-white">4</strong>YEARS OF RUNSAFARI</span>
          </div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">UPCOMING EVENTS</p>
            <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">Find Your Next Run.</h2>
          </div>
          <Link to="/events" data-testid="home-view-all-events" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-brand-cyan hover:gap-3 transition-all">VIEW ALL EVENTS <ArrowRight size={14} /></Link>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {upcoming.slice(0, 3).map((e) => <EventCard key={e.slug} event={e} onRegister={setRegEvent} />)}
        </div>
      </section>

      {/* WHY SPORTIVE */}
      <section className="border-y border-brand-line bg-brand-surface/40">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
          <p className="eyebrow">WHY SPORTIVE?</p>
          <h2 className="mt-3 max-w-2xl font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">More Than a Run. It's an Experience.</h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w, i) => (
              <div key={w.title} className="card-k p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-brand-olive/50">
                <span className="inline-flex rounded-xl bg-gradient-to-br from-brand-cyan/20 to-brand-olive/20 p-3 text-brand-cyan"><w.icon size={22} /></span>
                <h3 className="mt-4 font-display text-xl font-bold text-white">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RUNSAFARI */}
      <section className="grain relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 py-24 sm:px-6 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="eyebrow">OUR FLAGSHIP CONCEPT</p>
            <h2 className="mt-3 font-display text-5xl font-extrabold uppercase leading-none tracking-tight sm:text-6xl">
              <span className="text-brand-cyan">RUN</span><span className="text-white">SAFARI</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-300 sm:text-lg">
              RUNSAFARI is SPORTIVE's destination-running concept that combines running events with travel, adventure, culture and local experiences. Instead of simply travelling to a destination to participate in a race, RUNSAFARI encourages participants to experience the destination through the run.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[["RUN", "Challenge yourself across unique landscapes and routes.", "text-brand-cyan"],
                ["EXPLORE", "Discover places beyond conventional tourist circuits.", "text-brand-olive"],
                ["CONNECT", "Meet local communities, runners and travellers.", "text-brand-cyan"],
                ["EXPERIENCE", "Regional culture, food, heritage, nature and adventure.", "text-brand-olive"]].map(([t, d, c]) => (
                <div key={t} className="rounded-xl border border-brand-line bg-brand-ink/60 p-4">
                  <p className={`font-mono text-sm font-semibold tracking-[0.2em] ${c}`}>{t}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{d}</p>
                </div>
              ))}
            </div>
            <Link to="/runsafari" data-testid="home-runsafari-cta" className="btn-kinetic btn-primary-k mt-9">EXPLORE RUNSAFARI <ArrowRight size={16} /></Link>
          </div>
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1697222564085-6c3a135d0b46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHxfHxtYXJhdGhvbiUyMHRyYWlsJTIwcnVubmluZyUyMHN1bnNldCUyMG1vdW50YWlucyUyMGF0aGxldGV8ZW58MHx8fHwxNzg5MDY4MTE1fDA&ixlib=rb-4.1.0&q=85" alt="RUNSAFARI trail" className="rounded-3xl border border-brand-line object-cover shadow-[0_0_60px_rgba(24,168,221,0.15)]" />
            <div className="glass absolute -bottom-6 -left-4 rounded-2xl border border-brand-olive/30 px-6 py-4 sm:-left-8">
              <p className="font-mono text-[10px] tracking-[0.25em] text-brand-olive">RUN BEYOND THE ROUTE.</p>
              <p className="mt-1 font-display text-xl font-bold text-white">Run. Discover. Experience India.</p>
            </div>
          </div>
        </div>
      </section>

      {/* DESTINATIONS BENTO */}
      <section className="border-y border-brand-line bg-brand-surface/40">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
          <p className="eyebrow">DISCOVER DESTINATIONS</p>
          <h2 className="mt-3 max-w-2xl font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">Discover India — One Run at a Time.</h2>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {LANDSCAPES.map((l, i) => (
              <Link to={`/destinations#${l.tag.toLowerCase()}`} key={l.name} data-testid={`landscape-${l.tag.toLowerCase()}`}
                className={`group relative overflow-hidden rounded-2xl border border-brand-line ${i === 0 ? "sm:row-span-2 h-64 sm:h-auto" : "h-64"}`}>
                <img src={l.img} alt={l.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/30 to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <p className="font-mono text-[10px] tracking-[0.25em] text-brand-olive">{l.tag}</p>
                  <h3 className="mt-1 font-display text-2xl font-bold text-white">{l.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
        <p className="eyebrow">SPORTIVE IMPACT</p>
        <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">Beyond the Finish Line</h2>
        <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-brand-line bg-brand-line md:grid-cols-2 lg:grid-cols-5">
          {IMPACT.map(([n, t, d]) => (
            <div key={n} className="bg-brand-ink p-6 transition-colors hover:bg-brand-surface">
              <p className="font-mono text-sm text-brand-cyan">{n}</p>
              <h3 className="mt-3 font-display text-lg font-bold tracking-wide text-white">{t}</h3>
              <p className="mt-2 text-xs leading-relaxed text-slate-400">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      {featured.length > 0 && (
        <section className="border-y border-brand-line bg-brand-surface/40">
          <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="eyebrow">SELECTED RUNSAFARI PROJECTS</p>
                <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">Where We've Run</h2>
              </div>
              <Link to="/events" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-brand-olive hover:gap-3 transition-all">FULL EVENT ARCHIVE <ArrowRight size={14} /></Link>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {featured.slice(0, 6).map((e) => <EventCard key={e.slug} event={e} onRegister={setRegEvent} />)}
            </div>
          </div>
        </section>
      )}

      {/* PARTNERS MARQUEE */}
      <PartnersMarquee />

      {/* PARTNER CTA */}
      <section className="grain relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-cyan/10 via-transparent to-brand-olive/10" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 text-center sm:px-6">
          <p className="eyebrow">PARTNERS & COLLABORATION</p>
          <h2 className="mx-auto mt-3 max-w-3xl font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">Let's Create the Next Destination Run.</h2>
          <p className="mx-auto mt-5 max-w-2xl text-slate-400">Tourism departments, government bodies, brands, hotels, sports organisations, NGOs and media — SPORTIVE can work with you to develop a destination-based sporting experience.</p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link to="/partners" data-testid="home-partner-cta" className="btn-kinetic btn-primary-k">BECOME A PARTNER</Link>
            <Link to="/contact" data-testid="home-contact-cta" className="btn-kinetic btn-ghost-k">TALK TO US</Link>
          </div>
        </div>
      </section>

      {/* STORIES */}
      {stories.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow">SPORTIVE STORIES</p>
              <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">Stories From the Road</h2>
            </div>
            <Link to="/stories" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-brand-cyan hover:gap-3 transition-all">ALL STORIES <ArrowRight size={14} /></Link>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {stories.map((s) => (
              <Link to={`/stories/${s.slug}`} key={s.slug} data-testid={`story-card-${s.slug}`} className="card-k group overflow-hidden transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-brand-cyan/50">
                <div className="h-44 overflow-hidden">
                  <img src={s.image} alt={s.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                </div>
                <div className="p-5">
                  <p className="font-mono text-[10px] tracking-[0.2em] text-brand-olive">{s.category}</p>
                  <h3 className="mt-2 font-display text-xl font-bold leading-snug text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 line-clamp-2">{s.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* GALLERY MARQUEE */}
      {gallery.length > 0 && (
        <section className="overflow-hidden border-t border-brand-line py-16">
          <p className="eyebrow px-4 sm:px-6">EXPERIENCE SPORTIVE</p>
          <div className="mt-8 flex w-max animate-marquee gap-4">
            {[...gallery, ...gallery].map((g, i) => (
              <img key={i} src={g.url} alt={g.caption} className="h-52 w-80 rounded-xl border border-brand-line object-cover" loading="lazy" />
            ))}
          </div>
          <div className="mt-8 px-4 sm:px-6">
            <Link to="/gallery" data-testid="home-gallery-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-brand-cyan hover:gap-3 transition-all">OPEN GALLERY <ArrowRight size={14} /></Link>
          </div>
        </section>
      )}

      {regEvent && <RegisterModal event={regEvent} onClose={() => setRegEvent(null)} />}
    </div>
  );
}
